import { useApi } from "@/hooks/useApi";
import {
  QueryKey,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

type UseApiQueryOptions<TData, TError = Error> = Omit<
  UseQueryOptions<TData, TError, TData, QueryKey>,
  "queryKey" | "queryFn"
> & {
  key: QueryKey;
  path: string;
  params?: Record<string, string | number | boolean | undefined | null>;
};

/**
 * Wrap useQuery on top of the existing axios `useApi()` instance so the
 * Clerk Bearer token, Team-Id, Accept-Language and PostHog headers are
 * forwarded automatically. Callers only provide a stable cache key and the
 * API path — the rest follows the conventions of the legacy useApi() hook.
 */
export function useApiQuery<TData, TError = Error>({
  key,
  path,
  params,
  ...options
}: UseApiQueryOptions<TData, TError>): UseQueryResult<TData, TError> {
  const api = useApi();

  return useQuery<TData, TError, TData, QueryKey>({
    queryKey: key,
    queryFn: async () => {
      const { data } = await api.get<TData>(path, { params });
      return data;
    },
    ...options,
  });
}
